// src/features/settings/components/overenskomster-panel/OverenskomstEditDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO } from 'date-fns';
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material';

export default function OverenskomstEditDialog({ open, row, loading, onClose, onSubmit }) {
  const { t } = useTranslation(['settings', 'common']);

  const [values, setValues] = useState({
    name: '',
    code: '',
    valid_from: '',
    valid_to: '',
    pension_rate_employer: 0,
    pension_rate_employee: 0,
    fritvalg_pct: 0,
    education_hours_per_year: 0,
    barsel_foedende_weeks: 0,
    barsel_ikke_foedende_weeks: 0,
    senior_years_before_pension: 0,
    senior_trigger_age: '',
    forskudttid_rate: 0,
    assumes_scheduled_break: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (row) {
      setErrors({});
      setValues({
        name: row.name ?? '',
        code: row.code ?? '',
        valid_from: row.valid_from ?? '',
        valid_to: row.valid_to ?? '',
        pension_rate_employer: row.pension_rate_employer ?? 0,
        pension_rate_employee: row.pension_rate_employee ?? 0,
        fritvalg_pct: row.fritvalg_pct ?? 0,
        education_hours_per_year: row.education_hours_per_year ?? 0,
        barsel_foedende_weeks: row.barsel_foedende_weeks ?? 0,
        barsel_ikke_foedende_weeks: row.barsel_ikke_foedende_weeks ?? 0,
        senior_years_before_pension: row.senior_years_before_pension ?? 0,
        senior_trigger_age: row.senior_trigger_age ?? '',
        forskudttid_rate: row.forskudttid_rate ?? 0,
        assumes_scheduled_break: row.assumes_scheduled_break ?? false,
      });
    }
  }, [row]);

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleDateChange = (field) => (value) => {
    handleChange(field, value ? format(value, 'yyyy-MM-dd') : '');
  };

  const handleSubmit = () => {
    const errs = {};
    if (!values.name.trim()) errs.name = t('settings:overenskomster.validation.name_required');
    if (!values.code.trim()) errs.code = t('settings:overenskomster.validation.code_required');
    if (!values.valid_from) errs.valid_from = t('settings:overenskomster.validation.valid_from_required');
    if (!values.valid_to) errs.valid_to = t('settings:overenskomster.validation.valid_to_required');
    if (values.valid_from && values.valid_to && values.valid_to <= values.valid_from) {
      errs.valid_to = t('settings:overenskomster.validation.valid_to_after_valid_from');
    }
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    onSubmit({
      name: values.name,
      code: values.code,
      valid_from: values.valid_from,
      valid_to: values.valid_to,
      pension_rate_employer: Number(values.pension_rate_employer),
      pension_rate_employee: Number(values.pension_rate_employee),
      fritvalg_pct: Number(values.fritvalg_pct),
      education_hours_per_year: Number(values.education_hours_per_year),
      barsel_foedende_weeks: Number(values.barsel_foedende_weeks),
      barsel_ikke_foedende_weeks: Number(values.barsel_ikke_foedende_weeks),
      senior_years_before_pension: Number(values.senior_years_before_pension),
      senior_trigger_age: values.senior_trigger_age === '' ? null : Number(values.senior_trigger_age),
      forskudttid_rate: Number(values.forskudttid_rate),
      assumes_scheduled_break: values.assumes_scheduled_break,
    });
  };

  if (!row) return null;

  const numericFields = [
    ['pension_rate_employer', 0.01],
    ['pension_rate_employee', 0.01],
    ['fritvalg_pct', 0.01],
    ['education_hours_per_year', 1],
    ['barsel_foedende_weeks', 1],
    ['barsel_ikke_foedende_weeks', 1],
    ['senior_years_before_pension', 1],
    ['forskudttid_rate', 0.01],
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('settings:overenskomster.form.edit_title')}
        <Typography variant="caption" display="block" color="text.secondary">
          {row.code} — {row.name}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box pt={1}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t('settings:overenskomster.form.section_basic')}
          </Typography>

          <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
            <TextField
              label={t('settings:overenskomster.labels.code')}
              value={values.code}
              onChange={(e) => handleChange('code', e.target.value)}
              required
              error={Boolean(errors.code)}
              helperText={errors.code}
              inputProps={{ maxLength: 50 }}
              sx={{ width: 120 }}
            />
            <TextField
              label={t('settings:overenskomster.labels.name')}
              value={values.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              error={Boolean(errors.name)}
              helperText={errors.name}
              inputProps={{ maxLength: 255 }}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <>
              <DatePicker
                label={t('settings:overenskomster.labels.valid_from')}
                value={values.valid_from ? parseISO(values.valid_from) : null}
                onChange={handleDateChange('valid_from')}
                slotProps={{
                  textField: {
                    required: true,
                    error: Boolean(errors.valid_from),
                    helperText: errors.valid_from,
                    sx: { width: 160 },
                  },
                }}
              />
              <DatePicker
                label={t('settings:overenskomster.labels.valid_to')}
                value={values.valid_to ? parseISO(values.valid_to) : null}
                onChange={handleDateChange('valid_to')}
                slotProps={{
                  textField: {
                    required: true,
                    error: Boolean(errors.valid_to),
                    helperText: errors.valid_to,
                    sx: { width: 160 },
                  },
                }}
              />
            </>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t('settings:overenskomster.form.section_rates')}
          </Typography>

          <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
            {numericFields.map(([field, step]) => (
              <TextField
                key={field}
                label={t(`settings:overenskomster.labels.${field}`)}
                type="number"
                value={values[field]}
                onChange={(e) => handleChange(field, e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ min: 0, step }}
                sx={{ width: 200 }}
              />
            ))}
            <TextField
              label={t('settings:overenskomster.labels.senior_trigger_age')}
              type="number"
              value={values.senior_trigger_age}
              onChange={(e) => handleChange('senior_trigger_age', e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 200 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(values.assumes_scheduled_break)}
                  onChange={(e) => handleChange('assumes_scheduled_break', e.target.checked)}
                />
              }
              label={t('settings:overenskomster.labels.assumes_scheduled_break')}
              sx={{ alignSelf: 'center' }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onClose}
          disabled={loading}
        >
          {t('common:buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {t('common:buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
